import React from 'react';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Alert from '@material-ui/lab/Alert';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import DialogWithEnterAction from '../../components/DialogWithEnterAction';
import { useAddress } from '../../Hooks/addressHook';

const isPending = (setup) => setup?.state === 'waiting' || setup?.state === 'candidate';
const remainingSeconds = (retryAt) => Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
const successStyle = { backgroundColor: 'transparent', color: 'inherit' };
const groupSetupErrors = {
  group_administrator_required: 'telegram_group_administrator_required',
  telegram_unavailable: 'telegram_group_retry'
};

export function TelegramDestination({ telegram, candidate = false }) {
  const { t } = useTranslation();
  const group = ['group', 'supergroup'].includes(telegram.chat_type);
  const personal = telegram.chat_type === 'private';
  const type = group
    ? 'telegram_configuration_group'
    : personal
      ? 'telegram_configuration_personal'
      : 'unknown';
  return (
    <>
      <Typography gutterBottom>
        {t(candidate ? 'telegram_selected_configuration' : 'telegram_current_configuration')}:{' '}
        {t(telegram.chat_id ? type : 'not_set')}
      </Typography>
      {telegram.chat_id && (
        <Typography variant="body2" gutterBottom>
          {t(
            group
              ? 'telegram_group_name'
              : personal
                ? 'telegram_personal_name'
                : 'telegram_destination_name'
          )}
          : {telegram.display_name || telegram.chat_id}
        </Typography>
      )}
      {telegram.chat_id && !candidate && !telegram.verified_at && (
        <Typography variant="body2" gutterBottom>
          {t('telegram_unverified')}
        </Typography>
      )}
    </>
  );
}

export default function TelegramDialog({
  handleClose,
  user,
  setUser,
  testRetryAt,
  setTestRetryAt
}) {
  const { t } = useTranslation();
  const address = useAddress();
  const supported = user.telegram.onboarding !== undefined;
  const [setup, setSetup] = React.useState(null);
  const [busy, setBusy] = React.useState(supported);
  const [error, setError] = React.useState(null);
  const [test, setTest] = React.useState(null);
  const [showConnected, setShowConnected] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(() => remainingSeconds(testRetryAt));
  const mounted = React.useRef(false);
  const operation = React.useRef(null);
  const sequence = React.useRef(0);
  const inFlight = React.useRef(supported);
  const apiUrl = React.useRef(address.api_url);
  apiUrl.current = address.api_url;

  const request = React.useCallback(async (path, body) => {
    const response = await fetch(
      apiUrl.current(path),
      body === undefined
        ? undefined
        : {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
          }
    );
    const data = await response.json();
    if (!response.ok) {
      const failure = new Error(data.error);
      failure.status = response.status;
      failure.retryAfter = data.test?.retry_after_seconds;
      throw failure;
    }
    return data;
  }, []);

  const applyResult = React.useCallback(
    (data) => {
      if (data.telegram) setUser((previous) => ({ ...previous, telegram: data.telegram }));
      if (Object.prototype.hasOwnProperty.call(data, 'setup')) {
        operation.current = data.setup;
        setSetup(data.setup);
      }
      // Status can contain an old confirmation/test result. Feedback and retry
      // deadlines belong to the action response, not restored account state.
    },
    [setUser]
  );

  React.useEffect(() => {
    mounted.current = true;
    if (supported) {
      request('/api/telegram/status')
        .then((data) => {
          if (!mounted.current) return;
          applyResult(data);
        })
        .catch(() => {
          if (mounted.current) setError({ key: 'telegram_request_error' });
        })
        .finally(() => {
          if (mounted.current) {
            inFlight.current = false;
            setBusy(false);
          }
        });
    }
    return () => {
      mounted.current = false;
      if (isPending(operation.current)) {
        request('/api/telegram/cancel', { operation_id: operation.current.operation_id }).catch(
          () => {}
        );
      }
    };
  }, [supported, request, applyResult]);

  React.useEffect(() => {
    if (!isPending(setup) || busy) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const currentSequence = sequence.current;
      try {
        const data = await request('/api/telegram/status');
        if (!cancelled && mounted.current && currentSequence === sequence.current) {
          applyResult(data);
          setError((previous) => (previous?.polling ? null : previous));
        }
      } catch (failure) {
        if (!cancelled && mounted.current && currentSequence === sequence.current) {
          setError((previous) => previous || { key: 'telegram_request_error', polling: true });
          // Keep the candidate and retry polling after a transient failure.
          setSetup((previous) => ({ ...previous }));
        }
      }
    }, 2000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [setup, busy, request, applyResult]);

  React.useEffect(() => {
    setCooldown(remainingSeconds(testRetryAt));
    if (testRetryAt <= Date.now()) return;
    const timer = setInterval(() => {
      const seconds = remainingSeconds(testRetryAt);
      setCooldown(seconds);
      if (!seconds) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [testRetryAt]);

  const run = async (action, body, close = false) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    setTest(null);
    if (action === 'start' || action === 'disconnect') setShowConnected(false);
    const currentSequence = ++sequence.current;
    try {
      let data;
      try {
        data = await request(`/api/telegram/${action}`, body);
      } catch (failure) {
        if (action !== 'confirm' || failure.status) throw failure;
        // A lost response may follow a durable commit. Reconcile before offering retry.
        data = await request('/api/telegram/status');
        if (mounted.current && currentSequence === sequence.current) applyResult(data);
        if (data.setup?.state !== 'confirmed') throw failure;
      }
      if (!mounted.current) {
        if (action === 'start' && isPending(data.setup)) {
          request('/api/telegram/cancel', { operation_id: data.setup.operation_id }).catch(
            () => {}
          );
        }
        return;
      }
      if (currentSequence !== sequence.current) return;
      applyResult(data);
      if (action === 'confirm') setShowConnected(data.setup?.state === 'confirmed');
      if ((action === 'confirm' || action === 'test') && data.test) {
        setTest(data.test);
        setTestRetryAt(Date.now() + (data.test.retry_after_seconds || 0) * 1000);
      }
      if (close) handleClose();
    } catch (failure) {
      if (!mounted.current || currentSequence !== sequence.current) return;
      if (failure.retryAfter) setTestRetryAt(Date.now() + failure.retryAfter * 1000);
      setError({
        key:
          failure.message === 'group_administrator_required'
            ? 'telegram_group_administrator_required'
            : failure.status === 429
              ? 'telegram_cooldown'
              : failure.status === 403
                ? 'telegram_forbidden'
                : 'telegram_request_error'
      });
    } finally {
      if (mounted.current && currentSequence === sequence.current) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  };

  const close = () => {
    if (inFlight.current) return;
    if (isPending(operation.current)) {
      run('cancel', { operation_id: operation.current.operation_id }, true);
    } else handleClose();
  };

  const pending = isPending(setup);
  const connected = Boolean(user.telegram.chat_id);
  return (
    <DialogWithEnterAction
      open
      onClose={busy ? undefined : close}
      fullWidth
      maxWidth="sm"
      aria-labelledby="telegram-dialog-title"
    >
      <DialogTitle id="telegram-dialog-title">{t('change_telegram')}</DialogTitle>
      <DialogContent>
        <TelegramDestination telegram={user.telegram} />
        {supported && connected && !pending && (
          <Grid container spacing={1}>
            <Grid item>
              <Button
                onClick={() => run('test', {})}
                aria-describedby={cooldown > 0 ? 'telegram-test-retry' : undefined}
                disabled={
                  busy ||
                  !user.telegram.onboarding.available ||
                  !user.telegram.verified_at ||
                  cooldown > 0
                }
                color="primary"
              >
                {t('telegram_test')}
              </Button>
              {cooldown > 0 && (
                <Typography id="telegram-test-retry" variant="body2" gutterBottom>
                  {t('telegram_retry_after', { seconds: cooldown })}
                </Typography>
              )}
            </Grid>
            <Grid item>
              <Button onClick={() => run('disconnect', {})} disabled={busy} color="primary">
                {t('telegram_disconnect')}
              </Button>
            </Grid>
          </Grid>
        )}
        {!supported || !user.telegram.onboarding.available ? (
          <Typography gutterBottom>{t('telegram_unavailable')}</Typography>
        ) : (
          <>
            {!pending && (
              <>
                <Typography gutterBottom>
                  {t(connected ? 'telegram_change_destination' : 'telegram_connect')}
                </Typography>
                <Button
                  onClick={() => run('start', { mode: 'personal' })}
                  disabled={busy}
                  color="primary"
                >
                  {t('telegram_personal')}
                </Button>
                <Button
                  onClick={() => run('start', { mode: 'group' })}
                  disabled={busy}
                  color="primary"
                >
                  {t('telegram_group')}
                </Button>
              </>
            )}
            {pending && (
              <>
                {connected && (
                  <Typography gutterBottom>{t('telegram_replacement_pending')}</Typography>
                )}
                <Typography gutterBottom>
                  {t(setup.mode === 'group' ? 'telegram_group_steps' : 'telegram_personal_steps')}
                </Typography>
                {setup.mode === 'group' && setup.error && (
                  <Alert severity="warning">
                    {t(groupSetupErrors[setup.error] || 'telegram_request_error')}
                  </Alert>
                )}
                <Button href={setup.url} target="_blank" rel="noopener noreferrer" color="primary">
                  {t('telegram_open')}
                </Button>
                <div style={{ padding: 16, background: '#fff', width: 'fit-content' }}>
                  <QRCodeSVG value={setup.url} size={180} title={t('telegram_open')} />
                </div>
                <Typography variant="body2" gutterBottom>
                  {t('telegram_link_expires', {
                    time: new Date(setup.expires_at * 1000).toLocaleTimeString()
                  })}
                </Typography>
                {setup.state === 'waiting' ? (
                  <Typography gutterBottom>{t('telegram_waiting')}</Typography>
                ) : (
                  <>
                    <Typography gutterBottom>{t('telegram_confirm_destination')}</Typography>
                    <TelegramDestination telegram={setup.candidate} candidate />
                    <Button
                      onClick={() =>
                        run('confirm', {
                          operation_id: setup.operation_id,
                          chat_id: setup.candidate.chat_id
                        })
                      }
                      disabled={busy}
                      color="primary"
                    >
                      {t('telegram_confirm')}
                    </Button>
                  </>
                )}
              </>
            )}
            {setup?.state === 'expired' && <Alert severity="info">{t('telegram_expired')}</Alert>}
            {showConnected && (
              <Alert severity="success" style={successStyle}>
                {t('telegram_connected')}
              </Alert>
            )}
          </>
        )}
        {test && (
          <Alert
            severity={test.success ? 'success' : 'warning'}
            style={test.success ? successStyle : undefined}
          >
            {t(test.success ? 'telegram_test_success' : 'telegram_test_failure')}
          </Alert>
        )}
        {error && <Alert severity="error">{t(error.key)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy} color="primary">
          {t(pending ? 'cancel' : 'close')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}
